import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../config/api';
import { FaSave, FaTimes, FaEye } from 'react-icons/fa';
import './AdminPages.css';
import './AdminEmailTemplates.css';
import './AdminAutomatedEmails.css';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'color',
  'background',
  'align',
  'link',
];

const AdminAutomatedEmailEdit = () => {
  const { typeKey: typeKeyRaw } = useParams();
  const navigate = useNavigate();
  const typeKey = decodeURIComponent(typeKeyRaw || '');

  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);

  const [meta, setMeta] = useState(null);
  const [langTab, setLangTab] = useState('fr');
  const [fr, setFr] = useState({ subject: '', htmlBody: '' });
  const [it, setIt] = useState({ subject: '', htmlBody: '' });
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const res = await api.get('/settings/automated-emails');
        if (cancelled) return;

        const keys = res.data.keys || [];
        if (!keys.includes(typeKey)) {
          setInvalid(true);
          return;
        }

        setInvalid(false);
        setMeta(res.data.meta[typeKey]);
        const t = res.data.types[typeKey];
        setFr({
          subject: t?.fr?.subject || '',
          htmlBody: t?.fr?.htmlBody || '',
        });
        setIt({
          subject: t?.it?.subject || '',
          htmlBody: t?.it?.htmlBody || '',
        });
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          alert(err.response?.data?.error || 'Erreur lors du chargement');
          navigate('/admin/mailing-auto');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [typeKey, navigate]);

  const current = langTab === 'fr' ? fr : it;

  const setCurrentSubject = (v) =>
    langTab === 'fr' ? setFr((s) => ({ ...s, subject: v })) : setIt((s) => ({ ...s, subject: v }));

  const setCurrentBody = (v) =>
    langTab === 'fr' ? setFr((s) => ({ ...s, htmlBody: v })) : setIt((s) => ({ ...s, htmlBody: v }));

  const handleSave = async () => {
    if (!fr.subject.trim() || !fr.htmlBody.trim() || !it.subject.trim() || !it.htmlBody.trim()) {
      alert('Merci de remplir sujet et contenu pour le français ET l’italien.');
      return;
    }

    try {
      setSaving(true);
      await api.put(`/settings/automated-emails/${encodeURIComponent(typeKey)}`, { fr, it });
      navigate('/admin/mailing-auto');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erreur lors de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="admin-container">
        <div className="admin-header">
          <h1>Type inconnu</h1>
          <Link to="/admin/mailing-auto" className="btn-secondary">
            Retour
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="editor-header">
        <div>
          <p style={{ margin: '0 0 0.25rem', fontSize: '0.9rem' }}>
            <Link to="/admin/mailing-auto">← Mailing automatique</Link>
          </p>
          <h1 style={{ margin: 0 }}>{meta?.labelFr || typeKey}</h1>
          {meta?.labelIt && <p className="automated-email-card-sub">{meta.labelIt}</p>}
        </div>
        <div className="editor-actions">
          <button type="button" onClick={() => setShowPreview((p) => !p)} className="btn-secondary">
            <FaEye /> {showPreview ? 'Masquer aperçu' : 'Aperçu'}
          </button>
          <button type="button" onClick={handleSave} className="btn-primary" disabled={saving}>
            <FaSave /> {saving ? 'Envoi…' : 'Enregistrer'}
          </button>
          <Link to="/admin/mailing-auto" className="btn-secondary">
            <FaTimes /> Annuler
          </Link>
        </div>
      </div>

      {(meta?.hintFr || meta?.hintIt) && (
        <div className="automated-email-editor-meta">
          {langTab === 'fr' ? (
            <>
              <strong>Rappels (FR)</strong> — {meta.hintFr}
            </>
          ) : (
            <>
              <strong>Note (IT)</strong> — {meta.hintIt}
            </>
          )}
        </div>
      )}

      <div className="lang-tabs">
        <button type="button" className={`lang-tab ${langTab === 'fr' ? 'active' : ''}`} onClick={() => setLangTab('fr')}>
          Français
        </button>
        <button type="button" className={`lang-tab ${langTab === 'it' ? 'active' : ''}`} onClick={() => setLangTab('it')}>
          Italiano
        </button>
      </div>

      <div className={`editor-content ${showPreview ? 'with-preview' : ''}`}>
        <div className="email-template-editor editor-form-wrapper" style={{ boxShadow: 'none', padding: 0 }}>
          <div className="editor-form">
            <div className="form-group">
              <label>Sujet *</label>
              <input
                type="text"
                value={current.subject}
                onChange={(e) => setCurrentSubject(e.target.value)}
                placeholder="Objet tel qu’envoyé par messagerie"
              />
            </div>

            <div className="form-group">
              <label>Contenu HTML *</label>
              <div className="editor-help">
                <small>Variables entre accolades, ex.&nbsp;: {'{firstName}'},{' '} {'{clubName}'}</small>
              </div>
              <ReactQuill
                theme="snow"
                value={current.htmlBody}
                onChange={setCurrentBody}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                style={{ minHeight: '380px', marginBottom: '50px' }}
              />
            </div>
          </div>
        </div>

        {showPreview && (
          <div className="preview-pane">
            <h3>Aperçu ({langTab === 'fr' ? 'FR' : 'IT'})</h3>
            <div className="preview-content">
              <div className="preview-subject">
                <strong>Sujet :</strong> {current.subject || '(vide)'}
              </div>
              <div className="preview-html" dangerouslySetInnerHTML={{ __html: current.htmlBody || '<p>(vide)</p>' }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAutomatedEmailEdit;
